/**
 * Date & Time formatting utilities for event resources.
 *
 * Provides human-friendly date/time strings from the API event schema,
 * supporting single-day times, multi-day ranges, and all-day events.
 */

/**
 * Formats event date and time fields into a human-readable string.
 *
 * Handles the following scenarios:
 *   - Single day with start/end times: "Wed, Sep 17, 2025 • 7:00 PM – 11:00 PM"
 *   - Multi-day range:                 "Sep 17 – Sep 20, 2025"
 *   - All-day event:                   "Wed, Sep 17, 2025 • All Day"
 *   - Date only (no time):             "Wed, Sep 17, 2025"
 *
 * Falls back to `event_dates[0]` when top-level `overall_*` fields are absent.
 *
 * @param {Object} item - The event item containing date/time properties.
 * @returns {string} Formatted date/time string, or empty string if no date data exists.
 */
export function formatEventDateTime(item) {
  const prof = item?.profile || item || {};
  const firstDate = prof.event_dates?.[0] || item?.event_dates?.[0];

  const startDate = prof.overall_start_date || item?.overall_start_date || firstDate?.start_date;
  const startTime = prof.overall_start_time || item?.overall_start_time || firstDate?.start_time;
  const endDate = prof.overall_end_date || item?.overall_end_date || firstDate?.end_date;
  const endTime = prof.overall_end_time || item?.overall_end_time || firstDate?.end_time;
  // `all_day` is a boolean in the normalized event schema; older payloads sent 1/"true".
  const isAllDay = firstDate?.all_day === true || firstDate?.all_day === 1 || firstDate?.all_day === 'true';

  if (!startDate) return '';

  const startObj = new Date(`${startDate}T${startTime || '00:00:00'}`);

  // Guard against invalid date parsing
  if (isNaN(startObj.getTime())) return startDate;

  const dateStr = startObj.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  if (isAllDay) {
    return `${dateStr} \u2022 All Day`;
  }

  // Multi-day range (different start and end dates)
  if (endDate && endDate !== startDate) {
    const endObj = new Date(`${endDate}T00:00:00`);
    if (!isNaN(endObj.getTime())) {
      const startShort = startObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const endShort = endObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `${startShort} \u2013 ${endShort}`;
    }
  }

  // Single day with start time
  if (startTime) {
    const timeStr = startObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

    // Single day with start and end time
    if (endTime && (!endDate || endDate === startDate)) {
      const endObj = new Date(`${endDate || startDate}T${endTime}`);
      if (!isNaN(endObj.getTime())) {
        const endTimeStr = endObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        return `${dateStr} \u2022 ${timeStr} \u2013 ${endTimeStr}`;
      }
    }

    return `${dateStr} \u2022 ${timeStr}`;
  }

  return dateStr;
}

/**
 * Formats a validity period (e.g. of an offer) from YYYY-MM-DD dates.
 *
 * Handles the following scenarios:
 *   - Start and end:  "Sep 17 – Sep 20, 2025"
 *   - Same day:       "Wed, Sep 17, 2025"
 *   - Start only:     "From Sep 17, 2025"
 *   - End only:       "Until Sep 20, 2025"
 *
 * Unparseable dates are returned as they came.
 *
 * @param {string} [startDate] - First valid date.
 * @param {string} [endDate] - Last valid date.
 * @param {{ from: string, until: string }} [labels] - Templates with a `{date}` placeholder.
 * @returns {string} Formatted period, or empty string if no date exists.
 */
export function formatDateRange(startDate, endDate, labels = { from: 'From {date}', until: 'Until {date}' }) {
  const parse = (value) => {
    if (!value) return null;
    const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value);
    return isNaN(date.getTime()) ? null : date;
  };
  const longFormat = { month: 'short', day: 'numeric', year: 'numeric' };
  const format = (value, date) => (date ? date.toLocaleDateString('en-US', longFormat) : value);

  const startObj = parse(startDate);
  const endObj = parse(endDate);

  if (startDate && endDate) {
    if (startDate === endDate && startObj) {
      return startObj.toLocaleDateString('en-US', { weekday: 'short', ...longFormat });
    }
    const startShort = startObj ? startObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : startDate;
    return `${startShort} – ${format(endDate, endObj)}`;
  }
  if (startDate) return labels.from.replace('{date}', format(startDate, startObj));
  if (endDate) return labels.until.replace('{date}', format(endDate, endObj));
  return '';
}
